import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Check, FolderHeart, FolderPlus } from 'lucide-react';
import { api, errorMessage } from '../api/client';
import { useCollections, useCollectionsContaining, type CollectionDTO } from '../api/hooks';
import { Modal } from './Modal';
import { toast } from './Toasts';
import { queryKeys } from '../api/queryKeys';

export const SCOPE_LABEL = { personal: 'Personal', shared: 'Shared with me', public: 'Public' } as const;

/** Folder tree order: each folder followed by its subfolders, with depth. */
export function flattenTree<T extends CollectionDTO>(items: T[]): Array<T & { depth: number }> {
  const out: Array<T & { depth: number }> = [];
  const ids = new Set(items.map((x) => x.id));
  const walk = (parent: string | null, depth: number) => {
    for (const c of items.filter((x) => (parent === null ? !x.parentId || !ids.has(x.parentId) : x.parentId === parent))) {
      out.push({ ...c, depth });
      walk(c.id, depth + 1);
    }
  };
  walk(null, 0);
  return out;
}

/** Put this asset or sequence into one or more of your own collections. */
export function AddToCollectionButton({ subject, id, name, size = 'sm' }: { subject: 'asset' | 'sequence'; id: string; name: string; size?: 'sm' | 'md' }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button className={`btn ${size === 'sm' ? 'sm' : ''}`} onClick={() => setOpen(true)} data-testid="add-to-collection" title="Add to one of your collections">
        <FolderHeart size={14} /> Collect
      </button>
      {open && <AddToCollectionDialog subject={subject} id={id} name={name} onClose={() => setOpen(false)} />}
    </>
  );
}

/**
 * 1.2.0: several assets at once (the Library's selection) into a collection. Adding only: an asset already in the
 * collection stays as it is (the API ignores it), so a pick says how many were added.
 */
export function AddAssetsToCollectionDialog({ assetIds, onClose, onDone }: { assetIds: string[]; onClose: () => void; onDone?: () => void }) {
  const q = useCollections();
  const qc = useQueryClient();
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const [added, setAdded] = useState<Set<string>>(new Set());
  const editable = (q.data?.items ?? []).filter((c) => c.canEdit);
  const tree = (['personal', 'shared', 'public'] as const).flatMap((scope) => flattenTree(editable.filter((c) => c.scope === scope)).map((c, i) => ({ ...c, first: i === 0 })));
  const n = assetIds.length;
  const what = `${n} asset${n === 1 ? '' : 's'}`;

  const add = async (c: { id: string; name: string }) => {
    await api(`/api/v1/collections/${c.id}/items`, { method: 'POST', json: { assetIds } });
    await Promise.all([
      qc.invalidateQueries({ queryKey: queryKeys.collections() }),
      qc.invalidateQueries({ queryKey: ['collections-containing'] }),
      qc.invalidateQueries({ queryKey: ['collection-items'] }),
    ]);
    setAdded((s) => new Set(s).add(c.id));
    toast(`Added ${what} to ${c.name}`);
    onDone?.();
  };
  async function run(fn: () => Promise<void>) {
    setBusy(true);
    try { await fn(); } catch (err) { toast(errorMessage(err), 'error'); } finally { setBusy(false); }
  }
  const createAndAdd = () => {
    if (!newName.trim() || busy) return;
    void run(async () => {
      const c = await api<{ id: string; name: string }>('/api/v1/collections', { method: 'POST', json: { name: newName.trim() } });
      await add(c);
      setNewName('');
    });
  };

  return (
    <Modal title={`Add ${what} to a collection`} onClose={onClose} actions={<><Link className="btn ghost" to="/collections" onClick={onClose}>Open collections</Link><button className="btn primary" onClick={onClose}>Done</button></>}>
      <p className="muted" style={{ margin: 0 }} data-testid="bulk-collection-count">The {what} you selected go into the collection you pick; those already in it stay as they are.</p>
      {q.isLoading ? <div className="skeleton" style={{ height: 80 }} /> : (
        <div className="collection-picker" data-testid="collection-picker">
          {tree.length === 0 && <p className="faint" style={{ margin: 0 }}>You have no collections yet.</p>}
          {tree.map((c) => (<div key={c.id}>
            {c.first && <p className="section-label" style={{ margin: '8px 0 4px' }}>{SCOPE_LABEL[c.scope]}</p>}
            <button className={`collection-pick ${added.has(c.id) ? 'on' : ''}`} style={{ paddingLeft: 10 + c.depth * 18 }} disabled={busy}
              aria-pressed={added.has(c.id)} onClick={() => void run(() => add(c))} data-testid="collection-pick" data-name={c.name}>
              <span className="check">{added.has(c.id) && <Check size={12} />}</span>
              <span style={{ flex: 1, textAlign: 'left' }}>{c.name}</span>
              <span className="faint" style={{ fontSize: 12 }}>{c.assets + c.sequences}</span>
            </button>
          </div>))}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8 }}>
        <input className="input" style={{ flex: 1 }} placeholder="New collection" value={newName} aria-label="New collection name"
          onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && createAndAdd()} />
        <button className="btn" disabled={busy || !newName.trim()} data-testid="create-collection-and-add" onClick={createAndAdd}><FolderPlus size={14} /> Create and add</button>
      </div>
    </Modal>
  );
}

function AddToCollectionDialog({ subject, id, name, onClose }: { subject: 'asset' | 'sequence'; id: string; name: string; onClose: () => void }) {
  const q = useCollections();
  const containing = useCollectionsContaining(subject, id);
  const qc = useQueryClient();
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const inside = new Set(containing.data?.ids ?? []);
  const editable = (q.data?.items ?? []).filter((c) => c.canEdit);
  const tree = (['personal', 'shared', 'public'] as const).flatMap((scope) => flattenTree(editable.filter((c) => c.scope === scope)).map((c, i) => ({ ...c, first: i === 0 })));
  const body = subject === 'asset' ? { assetIds: [id] } : { sequenceIds: [id] };

  const refresh = () => Promise.all([
    qc.invalidateQueries({ queryKey: queryKeys.collections() }),
    qc.invalidateQueries({ queryKey: queryKeys.collectionsContaining(subject, id) }),
    qc.invalidateQueries({ queryKey: ['collection-items'] }),
  ]);

  async function toggle(c: CollectionDTO) {
    setBusy(true);
    try {
      await api(`/api/v1/collections/${c.id}/items${inside.has(c.id) ? '/remove' : ''}`, { method: 'POST', json: body });
      await refresh();
      toast(inside.has(c.id) ? `Removed from ${c.name}` : `Added to ${c.name}`);
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setBusy(false);
    }
  }

  async function createAndAdd() {
    if (!newName.trim()) return;
    setBusy(true);
    try {
      const c = await api<{ id: string; name: string }>('/api/v1/collections', { method: 'POST', json: { name: newName.trim() } });
      await api(`/api/v1/collections/${c.id}/items`, { method: 'POST', json: body });
      await refresh();
      setNewName('');
      toast(`Added to ${c.name}`);
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title={`Add “${name}” to a collection`} onClose={onClose} actions={<><Link className="btn ghost" to="/collections" onClick={onClose}>Open collections</Link><button className="btn primary" onClick={onClose}>Done</button></>}>
      <p className="muted" style={{ margin: 0 }}>Your own folders, folders shared with you to edit, and public collections you curate.</p>
      {q.isLoading ? <div className="skeleton" style={{ height: 80 }} /> : (
        <div className="collection-picker" data-testid="collection-picker">
          {tree.length === 0 && <p className="faint" style={{ margin: 0 }}>You have no collections yet.</p>}
          {tree.map((c) => (<div key={c.id}>
            {c.first && <p className="section-label" style={{ margin: '8px 0 4px' }}>{SCOPE_LABEL[c.scope]}</p>}
            <button key={c.id} className={`collection-pick ${inside.has(c.id) ? 'on' : ''}`} style={{ paddingLeft: 10 + c.depth * 18 }} disabled={busy}
              aria-pressed={inside.has(c.id)} onClick={() => void toggle(c)} data-testid="collection-pick">
              <span className="check">{inside.has(c.id) && <Check size={12} />}</span>
              <span style={{ flex: 1, textAlign: 'left' }}>{c.name}</span>
              <span className="faint" style={{ fontSize: 12 }}>{c.assets + c.sequences}</span>
            </button>
          </div>))}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8 }}>
        <input className="input" style={{ flex: 1 }} placeholder="New collection" value={newName} aria-label="New collection name"
          onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && void createAndAdd()} />
        <button className="btn" disabled={busy || !newName.trim()} onClick={() => void createAndAdd()} data-testid="create-collection-and-add"><FolderPlus size={14} /> Create and add</button>
      </div>
    </Modal>
  );
}
